import { useEffect, useState } from 'react';

import { getDaysOff } from '../settings/api';
import type { DayOff } from '../settings/models';
import { fetchRoleBudget, fetchTeamMember } from './api';
import type { Caller } from './models';

export type ActingAsState = {
    status: 'idle' | 'loading' | 'ready' | 'error';
    user: Caller | null;
    daysOff: Map<string, DayOff>;
    error: string | null;
};

const idle: ActingAsState = { status: 'idle', user: null, daysOff: new Map<string, DayOff>(), error: null };

// The member the actor acts for, loaded like the actor; idle when not acting.
export const useActingAsState = (memberId: string | null): ActingAsState => {
    const [state, setState] = useState<ActingAsState>(idle);

    useEffect(() => {
        if (!memberId) {
            setState(idle);
            return;
        }

        let cancelled = false;
        setState({ ...idle, status: 'loading' });

        (async () => {
            try {
                const member = await fetchTeamMember(memberId);
                const [budget, daysOff] = await Promise.all([fetchRoleBudget(member.department), getDaysOff(member.id)]);
                if (cancelled) {
                    return;
                }
                setState({
                    status: 'ready',
                    user: {
                        ...member,
                        avatarObjectUrl: null,
                        targetMinRatio: budget.billableMin,
                        targetMaxRatio: budget.billableMax,
                    },
                    daysOff: new Map(daysOff.items.map((d) => [d.date, d])),
                    error: null,
                });
            } catch (e) {
                if (!cancelled) {
                    setState({ ...idle, status: 'error', error: e instanceof Error ? e.message : 'Failed to load member.' });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [memberId]);

    return state;
};
